import * as Sentry from '@sentry/react';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import AuthProvider from '@/auth/AuthProvider';
import '@/i18n/i18n.ts';
import { queryClient } from '@/queryClient';
import { router } from '@/routes';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClientProvider } from '@tanstack/react-query';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router';
import { StrictMode } from 'react';
import { t } from 'i18next';
import { isReactScanEnabled, isSentryEnabled, isSentryFeedbackEnabled } from './utils/envVars';
import { createSentryFeedbackOptions } from './utils/sentryFeedback';
import { installToggleHaptics } from './utils/haptics';
import { installResumeRefetch } from './utils/appResume';
import { installAppLock } from './auth/biometric/appLock';
import { installAppUpdateCheck } from './appUpdate/appUpdateCheck';
import { installKeyboardAccessoryBar } from './utils/keyboard';
import { installSplashScreenFailsafe } from './utils/splashScreen';
import { installPushNotifications } from './push/pushNotifications';
import { captureAttribution } from '@/analytics/attribution';
import { initAnalytics } from '@/analytics/gtag';

// Capture first-touch campaign attribution before anything navigates, then load GA4. Both are
// web-only no-ops on native and when unconfigured.
captureAttribution();
initAnalytics();

if (isSentryFeedbackEnabled()) {
	// eslint-disable-next-line no-console
	console.log(
		`\u{1F482} Sentry feedback is ${isSentryEnabled() ? 'fully enabled' : 'enabled in dev-only feedback mode'}`,
	);

	const integrations: NonNullable<Parameters<typeof Sentry.init>[0]['integrations']> = [
		Sentry.feedbackIntegration(createSentryFeedbackOptions(t)),
	];

	if (isSentryEnabled()) {
		integrations.unshift(Sentry.replayIntegration(), Sentry.browserTracingIntegration());
	}

	Sentry.init({
		dsn: import.meta.env.VITE_SENTRY_DSN,
		integrations,

		// Setting this option to true will send default PII data to Sentry.
		// For example, automatic IP address collection on events
		sendDefaultPii: isSentryEnabled(),

		// Tracing
		tracesSampleRate: isSentryEnabled() ? 1.0 : 0,

		// Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
		tracePropagationTargets: isSentryEnabled() ? ['localhost', /^https:\/\/yourserver\.io\/api/] : [],

		// Session Replay - disabled outside full production mode
		replaysSessionSampleRate: 0,
		replaysOnErrorSampleRate: 0,
	});

	// Distinguish events coming from the Capacitor webview from regular web ones
	Sentry.setTag('platform', Capacitor.getPlatform());
}

if (Capacitor.isNativePlatform()) {
	// Android hardware/gesture back: navigate back through the router history
	// instead of Capacitor's default of closing the activity.
	void CapacitorApp.addListener('backButton', ({ canGoBack }) => {
		if (canGoBack) {
			window.history.back();
		} else {
			void CapacitorApp.exitApp();
		}
	});

	installToggleHaptics();
	installResumeRefetch();
	installAppLock();
	installAppUpdateCheck();
	installKeyboardAccessoryBar();
	installSplashScreenFailsafe();
	installPushNotifications(to => router.navigate(to));
	document.documentElement.classList.add('native');
}

// The literal `import.meta.env.DEV` check is what keeps the profiler out of the production
// bundle: Vite replaces it with `false`, so the dynamic import is dropped instead of emitted
// as a lazy chunk. `isReactScanEnabled` reads the opt-in env var.
if (import.meta.env.DEV && isReactScanEnabled()) {
	void import('react-scan').then(({ scan }) => scan({ enabled: true }));
}

const rootElement = document.getElementById('root');
if (rootElement && !rootElement.innerHTML) {
	const root = ReactDOM.createRoot(rootElement);
	root.render(
		<StrictMode>
			<QueryClientProvider client={queryClient}>
				<AuthProvider>
					<GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
						<RouterProvider router={router} />
					</GoogleOAuthProvider>
				</AuthProvider>
			</QueryClientProvider>
		</StrictMode>,
	);
}
